import React, { useState } from "react";
import { Typography, Box, Button, Alert } from "@mui/material";
import CommunicationTypesList from "../components/CommunicationTypesList";
import CommunicationTypeModal from "../components/CommunicationTypeModal";
import DetailModal from "../components/DetailModal";
import ConfirmationModal from "../components/ConfirmationModal";
import { CommunicationType } from "../../../data/CommunicationType";
import useCommunicationTypes from "../hooks/useCommunicationTypes";
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from "react-router-dom";

const CommunicationTypesPage: React.FC = () => {
    const {
        communicationTypes,
        loading,
        error,
        handleAdd,
        handleUpdate,
        handleDelete,
    } = useCommunicationTypes();

    // Modal states
    const [isModalOpen, setModalOpen] = useState(false);
    const [isDetailModalOpen, setDetailModalOpen] = useState(false);
    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [selectedCommunication, setSelectedCommunication] = useState<CommunicationType | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ✅ Open Add/Edit Modal
    const handleOpenAddModal = () => {
        setSelectedCommunication(null);
        setIsEditMode(false);
        setModalOpen(true);
    };

    const handleOpenEditModal = (type: CommunicationType) => {
        setSelectedCommunication(type);
        setIsEditMode(true);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedCommunication(null);
        setModalOpen(false);
    };

    // ✅ Open Detail Modal
    const handleViewDetails = (type: CommunicationType) => {
        setSelectedCommunication(type);
        setDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setSelectedCommunication(null);
        setDetailModalOpen(false);
    };

    // ✅ Open Confirmation Modal
    const handleOpenConfirmationModal = async (type: CommunicationType) => {
        setSelectedCommunication(type);
        setConfirmationModalOpen(true);
    };

    const handleCloseConfirmationModal = () => {
        setSelectedCommunication(null);
        setConfirmationModalOpen(false);
    };

    // ✅ Add or Update Communication Type
    const handleAddOrUpdate = async (updatedType: Partial<CommunicationType>) => {
        const payload: CommunicationType = {
            comTypeId: updatedType.comTypeId ?? uuidv4(),
            comType: updatedType.comType ?? "",
            icon: updatedType.icon ?? "",
            tags: updatedType.tags?.filter(tag => tag.trim() !== "") ?? [],
        };

        try {
            if (isEditMode && selectedCommunication) {
                await handleUpdate(payload); // ✅ handleUpdate calls addOrUpdateCommunicationType
            } else {
                await handleAdd(payload); // ✅ handleAdd calls addOrUpdateCommunicationType
            }

            handleCloseModal();
        } catch (error) {
            console.error("❌ Error saving communication type:", error);
        }
    };

    // ✅ Confirm Deletion
    const handleConfirmDelete = async () => {
        if (!selectedCommunication) {
            console.error("❌ No communication type selected for deletion.");
            return;
        }

        console.log("🛠️ Deleting communication type:", selectedCommunication.comTypeId);

        setDeleteLoading(true);
        try {
            await handleDelete(selectedCommunication.comTypeId);
            console.log("✅ Deleted successfully!");

            handleCloseConfirmationModal();
        } catch (error) {
            console.error("❌ Error deleting communication type:", error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const navigate = useNavigate();

    return (
        <Box sx={{ padding: 3, paddingTop: 10 }}>
            {loading && <Typography>Loading...</Typography>}
            {error && (
                <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Communication Types
                </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/communication")}
                        sx={{
                            backgroundColor: "#2196F3 !important",
                            color: "white !important",
                            "&:hover": { backgroundColor: "#1976D2 !important" },
                            padding: "6px 12px",
                            fontSize: "0.875rem",
                        }}
                    >
                        Back to Communication
                    </Button>
                <Button
                    variant="contained"
                    onClick={handleOpenAddModal}
                    sx={{
                        backgroundColor: "#4CAF50 !important",
                        color: "white !important",
                        "&:hover": {
                            backgroundColor: "#45A049 !important",
                        },
                    }}
                >
                    Add Communication Type
                </Button>
            </Box>

            {/* List */}
            <CommunicationTypesList
                communicationTypes={communicationTypes}
                onEdit={handleOpenEditModal}
                onDeleteClick={handleOpenConfirmationModal}
                onViewDetails={handleViewDetails}
                deleteLoading={deleteLoading}
            />

            {/* Add/Edit Modal */}
            <CommunicationTypeModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleAddOrUpdate}
                existingCommunicationTypes={communicationTypes}
                selectedCommunication={selectedCommunication}
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                onClose={handleCloseConfirmationModal}
                onConfirm={handleConfirmDelete}
                title="Delete Communication Type"
                message={`Are you sure you want to delete "${selectedCommunication?.comType}"?`}
                confirmLoading={deleteLoading}
            />

            {/* Detail Modal */}
            <DetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                selectedCommunication={selectedCommunication}
            />
        </Box>
    );
};

export default CommunicationTypesPage;
